import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { tenant } from '@tenant';
import { default as Alert } from 'antd/es/alert';
import { default as Modal } from 'antd/es/modal';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { InferType } from 'yup';

import { formatDecimalAmount } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';

import {
  useGetDisbursementWithdrawalAccountAuthorizations,
  usePostDisbursementWithdrawalRequests
} from 'api';
import type { WithdrawalAccount } from 'api';
import { withdrawalRequestDialogSchema as schema } from 'validators/withdrawalRequestDialog';

import { retryOnlyAfterRefresh } from 'api/mutationRetry';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { FormError } from '@atoms/FormError/FormError';
import { WithdrawFundsIcon } from '@atoms/Icon/WithdrawFundsIcon';
import { RollingLoader } from '@atoms/RollingLoader/RollingLoader';

import { InputField } from '@molecules/InputField';
import { LabelledInput } from '@molecules/LabelledInput';
import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';
import { SelectWithdrawalAccount } from '@molecules/SelectWithdrawalAccount';

import { errorLookupTable, WITHDRAW_MODAL } from './constants';
import { FXDetails } from './FXDetails';
import { InfoRow } from './InfoRow';
import {
  extractAmountFieldError,
  extractWithdrawalErrors
} from './withdrawalErrors';
import { WithdrawalSuccess } from './WithdrawalSuccess';

type Inputs = InferType<typeof schema>;

const lookupError = (errorKey: string) =>
  errorLookupTable[errorKey as keyof typeof errorLookupTable] || errorKey;

type Props = {
  withdrawalAccounts: WithdrawalAccount[];
  isWithdrawalAccountsError: boolean;
  isWithdrawalAccountsLoading: boolean;
  open: boolean;
  onClose: () => void;
};

export const WithdrawalRequestDialog = ({
  withdrawalAccounts,
  isWithdrawalAccountsError,
  isWithdrawalAccountsLoading,
  open,
  onClose
}: Props) => {
  const [confirmationPrompt, setConfirmationPrompt] = useState(false);
  const [isAccountSelected, setIsAccountSelected] = useState(false);

  // `isPending` is stale within the submitting render, so a same-tick double
  // click would pass it — the ref blocks the second POST.
  const inFlightRef = useRef(false);

  const { findAccountByAccountNumber } = useVirtualAccounts({
    filterCryptoAccounts: true
  });

  const organisationId = useOrganisationId<number>();
  const [isFXConfigured, setIsFXConfigured] = useState(true);
  const {
    data: { data: authorizedWithdrawalAccountRelations } = { data: undefined }
  } = useGetDisbursementWithdrawalAccountAuthorizations(organisationId, {
    withdrawal_account_status: 'active'
  });

  const {
    data: { data: withdrawalRequestData = undefined } = {},
    isPending: isWithdrawalDataLoading,
    isSuccess: creationSuccess,
    isError: creationError,
    error: creationErrorPayload,
    mutate: createWithdrawalRequest,
    reset: resetDialog
  } = usePostDisbursementWithdrawalRequests<unknown>({
    mutation: { retry: retryOnlyAfterRefresh }
  });

  const errorMessages = useMemo(
    () => (creationError ? extractWithdrawalErrors(creationErrorPayload) : []),
    [creationError, creationErrorPayload]
  );

  const {
    trigger,
    setError,
    formState,
    getValues,
    setValue,
    register,
    watch,
    formState: { errors },
    reset: resetForm
  } = useForm<Inputs>({
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const virtualAccountNumber = watch('virtual_account_number');

  const withdrawalAccountsOptions = useMemo(() => {
    const allowedWithdrawalUuids = authorizedWithdrawalAccountRelations
      ?.filter(
        (relation) =>
          relation.virtual_bank_account_number === virtualAccountNumber
      )
      .map((relation) => relation.withdrawal_account_uuid);

    return withdrawalAccounts.filter((withdrawalAccount) =>
      allowedWithdrawalUuids?.includes(withdrawalAccount.uuid)
    );
  }, [
    withdrawalAccounts,
    authorizedWithdrawalAccountRelations,
    virtualAccountNumber
  ]);

  const onWithdrawRequest = async (event: any) => {
    event.preventDefault();
    setConfirmationPrompt(true);
  };

  const onConfirmWithdrawal = (event: any) => {
    event.preventDefault();

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const data = getValues();
    createWithdrawalRequest(
      {
        organisationId: organisationId,
        data: {
          end_to_end_id: data.end_to_end_id,
          amount: Number.parseFloat(data.amount),
          currency_code: data.currency_code ?? 'EUR',
          creditor_account: {
            id: data.withdrawal_account.uuid
          },
          debtor_account: {
            scheme_name: 'VirtualAccount',
            id: data.virtual_account_number
          }
        }
      },
      {
        onSuccess: () => setConfirmationPrompt(false),
        onError: (error) => {
          const amountError = extractAmountFieldError(error);
          if (!hasValue(amountError)) return;

          setError('amount', { type: 'server', message: amountError });
          setConfirmationPrompt(false);
          resetDialog();
        },
        onSettled: () => {
          inFlightRef.current = false;
        }
      }
    );
  };

  const errorMessagesAsSet = useMemo(
    () => [...new Set(errorMessages)].map((error) => lookupError(error)),
    [errorMessages]
  );

  const onDialogClose = () => {
    resetForm();
    resetDialog();
    onClose();
    setConfirmationPrompt(false);
  };

  const payCurrencyCode = watch('currency_code');
  const buyCurrencyCode = getValues('withdrawal_account')?.currency as string;

  const isFX = useMemo(
    () =>
      Boolean(
        payCurrencyCode &&
          buyCurrencyCode &&
          payCurrencyCode !== buyCurrencyCode
      ),
    [payCurrencyCode, buyCurrencyCode]
  );

  const submitDisabled =
    formState.isSubmitSuccessful ||
    !formState.isValid ||
    !isFXConfigured ||
    isWithdrawalDataLoading;
  const withdrawalAccountsDisabled =
    !isAccountSelected ||
    !withdrawalAccountsOptions ||
    withdrawalAccountsOptions.length === 0;

  const formErrors = () => Object.keys(formState.errors).length > 0;
  const onVirtualAccountChange = (accountNumber: string) => {
    const account = findAccountByAccountNumber(accountNumber)!;
    setValue('virtual_account_number', account.account_number);
    setValue('currency_code', account.currency_code ?? 'EUR');
    setIsAccountSelected(true);
    formErrors() && trigger();
  };

  const onWithdrawalAccountsChange = (account: WithdrawalAccount) => {
    setValue('withdrawal_account', account);
    formErrors() && trigger();
  };

  const showForm =
    !isWithdrawalDataLoading &&
    !isWithdrawalAccountsError &&
    !creationSuccess &&
    !confirmationPrompt &&
    !creationError;

  return (
    <Modal
      destroyOnHidden
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-3 text-xl">
          <WithdrawFundsIcon stroke="black" className="size-6" />
          <div>Withdraw funds</div>
        </div>
      }
      styles={{
        content: {
          width: 440,
          paddingTop: 12,
          backgroundColor: 'rgb(255, 255, 255)'
        }
      }}
      footer={null}
    >
      {showForm && (
        <div className="flex flex-col justify-center">
          <form autoComplete="off" className="mt-3 flex flex-col gap-3">
            <LabelledInput label="Virtual Bank Account">
              <SelectVirtualAccount
                currentAccountNumber={getValues('virtual_account_number')}
                onChangeCallback={onVirtualAccountChange}
                e2eId="account-select"
                className="!h-12 !w-full"
                data-testid={WITHDRAW_MODAL.FROM_ACCOUNT}
                allowAll={false}
                filterTerminatedAccounts={true}
                filterSuspendedAccounts={true}
                filterCryptoAccounts={true}
              />
            </LabelledInput>
            <FormError
              message={formState.errors.virtual_account_number?.message}
            />
            <LabelledInput label="Withdrawal Account">
              {withdrawalAccountsDisabled && (
                <Alert
                  className="my-2 border-none bg-feedback-info-surface pl-2 text-content-primary"
                  message={
                    <>
                      Don't see any accounts?{' '}
                      <a
                        href={`mailto:${tenant.support.email}`}
                        className="text-feedback-info-strong underline"
                      >
                        Reach out to support
                      </a>{' '}
                      to configure one.
                    </>
                  }
                  type="info"
                  icon={
                    <ExclamationCircleIcon className="mb-auto size-5 rotate-180 !text-feedback-info-strong" />
                  }
                  showIcon
                />
              )}
              <SelectWithdrawalAccount
                currentAccount={watch('withdrawal_account')}
                onChangeCallback={onWithdrawalAccountsChange}
                accounts={withdrawalAccountsOptions}
                disabled={withdrawalAccountsDisabled}
                className="!h-12 !w-full"
                popupMatchSelectWidth={false}
                allowAll={false}
                data-testid={WITHDRAW_MODAL.TO_ACCOUNT}
              />
            </LabelledInput>

            <LabelledInput label={`Amount in ${watch('currency_code') ?? ''}`}>
              <InputField
                {...register('amount')}
                errorMessage={errors.amount?.message}
                data-testid={WITHDRAW_MODAL.AMOUNT}
              />
            </LabelledInput>

            <LabelledInput label="End-to-end ID">
              <InputField
                {...register('end_to_end_id')}
                data-testid={WITHDRAW_MODAL.END_TO_END_ID}
                errorMessage={errors.end_to_end_id?.message}
                placeholder="My chosen end-to-end ID"
              />
            </LabelledInput>
            <FXDetails
              visible={isFX}
              payCurrencyCode={payCurrencyCode}
              buyCurrencyCode={buyCurrencyCode}
              amount={watch('amount')}
              setIsFXConfigured={setIsFXConfigured}
              remountKey={watch('amount')?.toString()}
            />
            <PrimaryButton
              size="large"
              className="w-full"
              data-testid={WITHDRAW_MODAL.WITHDRAW_BUTTON}
              disabled={!formState.isValid}
              onClick={onWithdrawRequest}
            >
              Request withdrawal
            </PrimaryButton>
          </form>
        </div>
      )}
      {confirmationPrompt && !creationSuccess && !creationError && (
        <div>
          <div className="flex flex-col gap-2 rounded-2xl bg-surface-muted p-6">
            <InfoRow
              label="Amount"
              value={formatDecimalAmount(
                Number.parseFloat(getValues('amount').toString())
              )}
              subValue={getValues('currency_code')}
            />
            <InfoRow
              label="Bank"
              value={getValues('withdrawal_account')?.bank_name}
            />
            <InfoRow
              label={
                getValues('withdrawal_account')?.local_instrument !== 'CRYPTO'
                  ? 'Account number'
                  : 'Wallet address'
              }
              value={getValues('withdrawal_account')?.account_number}
            />
            <InfoRow
              label="IBAN"
              value={getValues('withdrawal_account')?.iban}
            />
            <InfoRow
              label="Beneficiary"
              value={
                getValues('withdrawal_account')?.party_identification?.name
              }
            />
            <InfoRow label="End-to-end ID" value={getValues('end_to_end_id')} />
          </div>
          <FXDetails
            visible={isFX}
            payCurrencyCode={payCurrencyCode}
            buyCurrencyCode={buyCurrencyCode}
            amount={watch('amount')}
            setIsFXConfigured={setIsFXConfigured}
            remountKey={watch('amount')?.toString()}
          />
          <PrimaryButton
            className="mt-10 w-full"
            htmlType="submit"
            disabled={submitDisabled}
            onClick={onConfirmWithdrawal}
            data-testid={WITHDRAW_MODAL.CONFIRM_BUTTON}
          >
            Confirm request
          </PrimaryButton>
          <SecondaryButton
            className="mt-4 w-full"
            onClick={() => {
              setConfirmationPrompt(false);
            }}
          >
            Back
          </SecondaryButton>
        </div>
      )}
      {isWithdrawalDataLoading &&
        isWithdrawalAccountsLoading &&
        !creationSuccess && (
          <div className="my-8 flex justify-center p-4">
            <RollingLoader ariaLabel="Creating withdrawal" />
          </div>
        )}
      {creationSuccess && withdrawalRequestData && !isWithdrawalDataLoading && (
        <WithdrawalSuccess
          onDialogClose={onDialogClose}
          withdrawalRequestData={withdrawalRequestData}
        />
      )}
      {creationError && (
        <div className="rounded-b-lg bg-surface-muted p-6">
          <div className="rounded-2xl bg-feedback-error-surface p-3 text-feedback-error">
            {errorMessagesAsSet.map((error) => (
              <div key={error}>- {error}</div>
            ))}
          </div>
          <SecondaryButton
            className="mt-6 w-full"
            onClick={() => {
              resetForm();
              resetDialog();
              setConfirmationPrompt(false);
            }}
          >
            Back
          </SecondaryButton>
        </div>
      )}
    </Modal>
  );
};
